import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { getHolesByCourse, getStrokesForHole, calculateStablefordPoints } from './courseData';
import { createNotification } from './notifications';


function MarkerDashboard({ onClose, currentUserId }) {
  const [pendingRounds, setPendingRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'rounds'),
      where('markerId', '==', currentUserId),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const rounds = await Promise.all(
        snapshot.docs.map(async (roundDoc) => {
          const roundData = { id: roundDoc.id, ...roundDoc.data() };
          
          const userDoc = await getDoc(doc(db, 'users', roundData.userId));
          roundData.playerName = userDoc.exists() ? userDoc.data().name : 'Unknown';
          
          return roundData;
        })
      );
      
      setPendingRounds(rounds.sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      ));
    });

    return unsubscribe;
  }, [currentUserId]);

  const handleViewRound = (round) => {
    setSelectedRound(round);
  };

  const handleApprove = () => {
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    if (!selectedRound?.id) {
      alert('Error: Round ID is missing');
      return;
    }
    
    setLoading(true);
    try {
      await updateDoc(doc(db, 'rounds', selectedRound.id), {
        status: 'approved',
        approvedAt: new Date().toISOString()
      });

      try {
        await createNotification(selectedRound.userId, 'score_approved', { roundId: selectedRound.id, date: selectedRound.date });
      } catch (notifError) {
        console.error('Error sending approval notification:', notifError);
      }

      setSelectedRound(null);
      setShowApproveModal(false);
    } catch (error) {
      console.error('Error approving round:', error);
      alert('Error approving round: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDispute = () => {
    setShowDisputeModal(true);
  };

  const confirmDispute = async () => {
    if (!disputeReason.trim()) {
      alert('Please enter a reason for disputing');
      return;
    }
    
    if (!selectedRound?.id) {
      alert('Error: Round ID is missing');
      return;
    }
    
    setLoading(true);
    try {
      await updateDoc(doc(db, 'rounds', selectedRound.id), {
        status: 'disputed',
        disputedAt: new Date().toISOString(),
        disputeReason: disputeReason
      });

      try {
        await createNotification(selectedRound.userId, 'score_disputed', { roundId: selectedRound.id, reason: disputeReason });
      } catch (notifError) {
        console.error('Error sending dispute notification:', notifError);
      }

      setSelectedRound(null);
      setShowDisputeModal(false);
      setDisputeReason('');
    } catch (error) {
      console.error('Error disputing round:', error);
      alert('Error disputing round: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (selectedRound) {
    const holes = getHolesByCourse(selectedRound.course);
    
    return (
      <div style={styles.overlay}>
        <div style={styles.cardWide}>
          <div style={styles.header}>
            <h2 style={styles.title}>Review Scorecard</h2>
            <button onClick={() => setSelectedRound(null)} style={styles.closeButton}>✕</button>
          </div>

          <div style={styles.scrollContent}>
            <div style={styles.playerInfo}>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Player:</span>
                <span style={styles.value}>{selectedRound.playerName}</span>
              </div>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Course:</span>
                <span style={styles.value}>{selectedRound.course}</span>
              </div>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Date:</span>
                <span style={styles.value}>
                  {new Date(selectedRound.date).toLocaleDateString()}
                </span>
              </div>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Handicap:</span>
                <span style={styles.value}>
                  {selectedRound.handicap} (Course: {selectedRound.courseHandicap})
                </span>
              </div>
            </div>

            <div style={styles.summary}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Gross Score</div>
                <div style={styles.summaryValue}>{selectedRound.totalGross}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Stableford Points</div>
                <div style={styles.summaryValue}>{selectedRound.totalStableford}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Total Putts</div>
                <div style={styles.summaryValue}>{selectedRound.totalPutts}</div>
              </div>
            </div>

            <h3 style={styles.sectionTitle}>📋 Scorecard</h3>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Hole</th>
                    <th style={styles.th}>Par</th>
                    <th style={styles.th}>Strokes</th>
                    <th style={styles.th}>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {holes.map(hole => {
                    const data = selectedRound.holeData[hole.number] || {};
                    const strokesReceived = getStrokesForHole(hole.strokeIndex, selectedRound.courseHandicap, selectedRound.course);
                    const points = data.strokes ? calculateStablefordPoints(data.strokes, hole.par, strokesReceived) : '-';
                    
                    return (
                      <tr key={hole.number} style={hole.number % 2 === 0 ? styles.trEven : styles.tr}>
                        <td style={styles.tdHole}>{hole.number}</td>
                        <td style={styles.td}>{hole.par}</td>
                        <td style={styles.tdStrokes}>{data.strokes || '-'}</td>
                        <td style={{
                          ...styles.tdPoints,
                          color: points === '-' ? '#718096' : points > 2 ? '#48bb78' : points < 2 ? '#e53e3e' : '#3182ce',
                          backgroundColor: points === '-' ? 'transparent' : points > 2 ? 'rgba(72,187,120,0.1)' : points < 2 ? 'rgba(229,83,83,0.1)' : 'rgba(49,130,206,0.1)'
                        }}>
                          {points}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={styles.totalRow}>
                    <td style={styles.totalCell} colSpan="2"><strong>TOTAL</strong></td>
                    <td style={{...styles.totalCell, fontSize: '20px'}}>
                      <strong>{selectedRound.totalGross}</strong>
                    </td>
                    <td style={{...styles.totalCell, fontSize: '20px'}}>
                      <strong>{selectedRound.totalStableford}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={styles.actions}>
            <button
              onClick={handleDispute}
              style={styles.disputeButton}
              disabled={loading}
            >
              ❌ Dispute Scorecard
            </button>
            <button
              onClick={handleApprove}
              style={styles.approveButton}
              disabled={loading}
            >
              ✅ Approve Scorecard
            </button>
          </div>
        </div>

        {/* Approve Confirmation Modal */}
        {showApproveModal && (
          <div style={styles.confirmOverlay}>
            <div style={styles.confirmCard}>
              <h3 style={styles.confirmTitle}>✅ Approve Scorecard?</h3>
              <p style={styles.confirmText}>
                This will approve <strong>{selectedRound?.playerName}'s</strong> scorecard and add it to the leaderboard.
              </p>
              <div style={styles.confirmButtons}>
                <button onClick={() => setShowApproveModal(false)} style={styles.confirmCancel}>
                  Cancel
                </button>
                <button onClick={confirmApprove} style={styles.confirmApprove} disabled={loading}>
                  {loading ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dispute Modal */}
        {showDisputeModal && (
          <div style={styles.confirmOverlay}>
            <div style={styles.confirmCard}>
              <h3 style={styles.confirmTitle}>❌ Dispute Scorecard?</h3>
              <p style={styles.confirmText}>
                Please explain why you're disputing <strong>{selectedRound?.playerName}'s</strong> scorecard. They will see this message.
              </p>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="e.g., Incorrect score on hole 5, or discrepancy in putts"
                style={styles.disputeTextarea}
                rows="4"
                autoFocus
              />
              <div style={styles.confirmButtons}>
                <button onClick={() => {
                  setShowDisputeModal(false);
                  setDisputeReason('');
                }} style={styles.confirmCancel}>
                  Cancel
                </button>
                <button onClick={confirmDispute} style={styles.confirmDispute} disabled={loading}>
                  {loading ? 'Disputing...' : 'Dispute'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Pending Approvals</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {pendingRounds.length === 0 ? (
          <div style={styles.emptyState}>
            <p>✅ No pending scorecards to review</p>
            <p style={styles.emptySubtext}>You'll see rounds here when someone selects you as their marker.</p>
          </div>
        ) : (
          <div style={styles.roundsList}>
            {pendingRounds.map(round => (
              <div key={round.id} style={styles.roundCard} onClick={() => handleViewRound(round)}>
                <div style={styles.roundHeader}>
                  <h3 style={styles.roundPlayer}>{round.playerName}</h3>
                  <span style={styles.roundDate}>
                    {new Date(round.date).toLocaleDateString()}
                  </span>
                </div>
                <div style={styles.roundDetails}>
                  <span>{round.course}</span>
                  <span>•</span>
                  <span>{round.totalGross} strokes</span>
                  <span>•</span>
                  <span>{round.totalStableford} points</span>
                </div>
                <div style={styles.viewButton}>
                  Review Scorecard →
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  cardWide: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  scrollContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 30px'
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#6b7280',
    fontWeight: '400'
  },
  emptySubtext: {
    fontSize: '14px',
    marginTop: '10px',
    color: '#6b7280'
  },
  roundsList: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  roundCard: {
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '15px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  roundHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  roundPlayer: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  roundDate: {
    fontSize: '14px',
    color: '#9ca3af',
    fontWeight: '400'
  },
  roundDetails: {
    display: 'flex',
    gap: '8px',
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '10px',
    fontWeight: '500'
  },
  viewButton: {
    color: '#d4af37',
    fontSize: '14px',
    fontWeight: '600'
  },
  playerInfo: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
    border: '1px solid #2d2d2d'
  },
  playerDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  label: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  value: {
    fontSize: '16px',
    color: '#ffffff',
    fontWeight: '600'
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: '30px'
  },
  summaryItem: {
    textAlign: 'center'
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginBottom: '5px',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  summaryValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '15px',
    letterSpacing: '-0.01em'
  },
  tableContainer: {
    overflowX: 'auto',
    marginBottom: '20px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    backgroundColor: '#0f0f0f',
    color: '#d4af37',
    padding: '14px 10px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  tr: {
    borderBottom: '1px solid #2d2d2d',
    backgroundColor: '#1e1e1e'
  },
  trEven: {
    borderBottom: '1px solid #2d2d2d',
    backgroundColor: '#1a1a1a'
  },
  td: {
    padding: '14px 10px',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '15px',
    fontWeight: '500'
  },
  tdHole: {
    padding: '14px 10px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '15px',
    color: '#ffffff'
  },
  tdStrokes: {
    padding: '14px 10px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '18px',
    color: '#ffffff'
  },
  tdPoints: {
    padding: '14px 10px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '18px',
    borderRadius: '4px',
    transition: 'all 0.2s'
  },
  totalRow: {
    backgroundColor: '#0f0f0f',
    borderTop: '2px solid #d4af37'
  },
  totalCell: {
    padding: '16px 10px',
    textAlign: 'center',
    fontWeight: '700',
    color: '#d4af37',
    fontSize: '15px'
  },
  actions: {
    display: 'flex',
    gap: '15px',
    padding: '20px 30px',
    borderTop: '1px solid #2d2d2d',
    flexShrink: 0
  },
  approveButton: {
    flex: 1,
    padding: '15px',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  disputeButton: {
    flex: 1,
    padding: '15px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  confirmOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001
  },
  confirmCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '500px',
    width: '90%',
    margin: '20px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d'
  },
  confirmTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '15px',
    textAlign: 'center',
    letterSpacing: '-0.01em'
  },
  confirmText: {
    fontSize: '15px',
    color: '#9ca3af',
    marginBottom: '20px',
    lineHeight: '1.6',
    fontWeight: '400'
  },
  disputeTextarea: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    marginBottom: '20px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  confirmButtons: {
    display: 'flex',
    gap: '15px'
  },
  confirmCancel: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  confirmApprove: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  confirmDispute: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  }
};

export default MarkerDashboard;